import { useState, useRef, useEffect, useCallback, FormEvent } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import styles from './NewUploadDialog.module.css';
import {
  orderDateToRfc3339,
  validateBackfillOrderNumber,
  validateOrderNumber,
  validateShopifyId,
} from '../utils/orderValidation';

export interface NewUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onResolved: (orderId: string) => void;
}

type Mode = 'shopify' | 'backfill' | 'walkin';

/**
 * The New Upload dialog. Its form is mounted only while it's open, so closing
 * it discards whatever was typed and the next open starts fresh.
 */
export function NewUploadDialog({ open, onClose, onResolved }: NewUploadDialogProps) {
  return open ? <NewUploadForm onClose={onClose} onResolved={onResolved} /> : null;
}

function NewUploadForm({ onClose, onResolved }: Omit<NewUploadDialogProps, 'open'>) {
  const [mode, setMode] = useState<Mode>('shopify');
  const [orderNumber, setOrderNumber] = useState('');
  // Walk-in fields — a counter order with no Shopify order behind it.
  const [walkinEmail, setWalkinEmail] = useState('');
  const [walkinName, setWalkinName] = useState('');
  const [walkinLabel, setWalkinLabel] = useState('');
  // Backfill fields — every value the resolve path would have read from Shopify.
  const [backfillNumber, setBackfillNumber] = useState('');
  const [backfillOrderId, setBackfillOrderId] = useState('');
  const [backfillCustomerId, setBackfillCustomerId] = useState('');
  const [backfillEmail, setBackfillEmail] = useState('');
  const [backfillName, setBackfillName] = useState('');
  const [backfillDate, setBackfillDate] = useState('');
  const [backfillFulfilment, setBackfillFulfilment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { handleUnauthorized } = useAdminAuth();

  // Focus the first field once the dialog has rendered
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Escape key handler
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleOrderNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderNumber(e.target.value);
    setValidationError(null);
    setError(null);
  }, []);

  const switchMode = useCallback((next: Mode) => {
    setMode(next);
    setError(null);
    setValidationError(null);
  }, []);

  const submitShopify = useCallback(async () => {
    const trimmed = orderNumber.trim();
    const validationResult = validateOrderNumber(trimmed);
    if (validationResult) {
      setValidationError(validationResult);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/v1/admin/orders/resolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ order_number: trimmed }),
      });

      if (res.ok) {
        const data = await res.json();
        onResolved(data.order_id);
        return;
      }

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      switch (res.status) {
        case 404:
          setError('Order not found in Shopify. Check the order number and try again.');
          break;
        case 502:
          setError('Unable to look up order. Please try again later.');
          break;
        default:
          setError('An unexpected error occurred. Please try again.');
          break;
      }
    } catch {
      setError('Network error. Check your connection.');
    } finally {
      setIsLoading(false);
    }
  }, [orderNumber, onResolved, handleUnauthorized]);

  const submitWalkin = useCallback(async () => {
    if (walkinEmail.trim().length === 0) {
      setValidationError('Customer email is required.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/v1/admin/orders/walkin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customer_email: walkinEmail.trim(),
          customer_name: walkinName.trim() || undefined,
          label: walkinLabel.trim() || undefined,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        onResolved(data.order_id);
        return;
      }

      if (res.status === 401) {
        handleUnauthorized();
        return;
      }

      // Unlike the other modes, this endpoint answers 403 itself: the Shopify
      // admin app is missing the customer scopes. That is an actionable message
      // for the admin, not a sign the session has lapsed — so it is shown, and
      // only a bare 403 with no body is treated as an auth failure.
      if (res.status === 400 || res.status === 403 || res.status === 502) {
        const data = await res.json().catch(() => null);
        if (res.status === 403 && !data?.error) {
          handleUnauthorized();
          return;
        }
        setError(data?.error ?? 'Please check the details and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } catch {
      setError('Network error. Check your connection.');
    } finally {
      setIsLoading(false);
    }
  }, [walkinEmail, walkinName, walkinLabel, onResolved, handleUnauthorized]);

  const submitBackfill = useCallback(async () => {
    const numberError = validateBackfillOrderNumber(backfillNumber);
    const orderIdError = validateShopifyId(backfillOrderId, 'Shopify order ID');
    const customerIdError = validateShopifyId(backfillCustomerId, 'Customer ID');
    const firstError =
      numberError ??
      orderIdError ??
      customerIdError ??
      (backfillEmail.trim().length === 0 ? 'Customer email is required.' : null) ??
      (backfillDate.length === 0 ? 'Order date is required.' : null);

    if (firstError) {
      setValidationError(firstError);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/v1/admin/orders/backfill', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          order_number: backfillNumber.trim(),
          shopify_order_id: backfillOrderId.trim(),
          customer_id: backfillCustomerId.trim(),
          customer_email: backfillEmail.trim(),
          order_date: orderDateToRfc3339(backfillDate),
          customer_name: backfillName.trim() || undefined,
          // Omitted rather than sent empty: the API reads a missing status as
          // Shopify's null, which means unfulfilled.
          fulfillment_status: backfillFulfilment || undefined,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        onResolved(data.order_id);
        return;
      }

      // The backfill endpoint itself never answers 403 — one here is the
      // authoriser rejecting the admin session, same as everywhere else.
      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      // 400 and 409 both carry a specific, actionable message (a bad field, or an
      // order already filed under another customer) — show it rather than a
      // generic failure the admin cannot act on.
      if (res.status === 400 || res.status === 409) {
        const data = await res.json().catch(() => null);
        setError(data?.error ?? 'Please check the details and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } catch {
      setError('Network error. Check your connection.');
    } finally {
      setIsLoading(false);
    }
  }, [
    backfillNumber,
    backfillOrderId,
    backfillCustomerId,
    backfillEmail,
    backfillName,
    backfillDate,
    backfillFulfilment,
    onResolved,
    handleUnauthorized,
  ]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (mode === 'shopify') {
        void submitShopify();
      } else if (mode === 'backfill') {
        void submitBackfill();
      } else {
        void submitWalkin();
      }
    },
    [mode, submitShopify, submitBackfill, submitWalkin],
  );

  const isSubmitDisabled =
    isLoading ||
    (mode === 'shopify' && orderNumber.trim().length === 0) ||
    (mode === 'walkin' && walkinEmail.trim().length === 0);

  const SUBMIT_LABELS: Record<Mode, { idle: string; busy: string }> = {
    shopify: { idle: 'Submit', busy: 'Resolving...' },
    backfill: { idle: 'Add Order', busy: 'Adding...' },
    walkin: { idle: 'Create Walk-in Order', busy: 'Creating...' },
  };
  const submitLabel = isLoading ? SUBMIT_LABELS[mode].busy : SUBMIT_LABELS[mode].idle;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="New Upload">
      <div className={styles.dialog}>
        <h2 className={styles.title}>New Upload</h2>

        <div className={styles.modeToggle} role="tablist" aria-label="Order type">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'shopify'}
            className={`${styles.modeButton} ${mode === 'shopify' ? styles.modeButtonActive : ''}`}
            onClick={() => switchMode('shopify')}
            disabled={isLoading}
          >
            Shopify order
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'backfill'}
            className={`${styles.modeButton} ${mode === 'backfill' ? styles.modeButtonActive : ''}`}
            onClick={() => switchMode('backfill')}
            disabled={isLoading}
          >
            Older order
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'walkin'}
            className={`${styles.modeButton} ${mode === 'walkin' ? styles.modeButtonActive : ''}`}
            onClick={() => switchMode('walkin')}
            disabled={isLoading}
          >
            Walk-in
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {mode === 'shopify' && (
            <>
              <label htmlFor="order-number-input" className={styles.label}>
                Order Number
              </label>
              <input
                id="order-number-input"
                ref={inputRef}
                type="text"
                className={styles.input}
                value={orderNumber}
                onChange={handleOrderNumberChange}
                placeholder="e.g. CDL1112"
                maxLength={64}
                disabled={isLoading}
                aria-invalid={!!validationError}
                aria-describedby={validationError ? 'validation-error' : undefined}
              />
            </>
          )}

          {mode === 'backfill' && (
            <>
              <p className={styles.hint}>
                For an order Shopify won&apos;t return — its API only exposes the last 60
                days. Copy the details from the order&apos;s page in the Shopify admin. If
                Shopify does still have the order, its values are used instead of these.
              </p>

              <label htmlFor="backfill-number-input" className={styles.label}>
                Order Number
              </label>
              <input
                id="backfill-number-input"
                ref={inputRef}
                type="text"
                className={styles.input}
                value={backfillNumber}
                onChange={(e) => {
                  setBackfillNumber(e.target.value);
                  setValidationError(null);
                  setError(null);
                }}
                placeholder="e.g. #CDL1014"
                maxLength={64}
                disabled={isLoading}
              />

              <label htmlFor="backfill-order-id-input" className={styles.label}>
                Shopify Order ID
              </label>
              <input
                id="backfill-order-id-input"
                type="text"
                inputMode="numeric"
                className={styles.input}
                value={backfillOrderId}
                onChange={(e) => {
                  setBackfillOrderId(e.target.value);
                  setValidationError(null);
                  setError(null);
                }}
                placeholder="the digits at the end of the order's admin URL"
                disabled={isLoading}
              />

              <label htmlFor="backfill-customer-id-input" className={styles.label}>
                Customer ID
              </label>
              <input
                id="backfill-customer-id-input"
                type="text"
                inputMode="numeric"
                className={styles.input}
                value={backfillCustomerId}
                onChange={(e) => {
                  setBackfillCustomerId(e.target.value);
                  setValidationError(null);
                  setError(null);
                }}
                placeholder="the digits at the end of the customer's admin URL"
                disabled={isLoading}
              />

              <label htmlFor="backfill-email-input" className={styles.label}>
                Customer Email
              </label>
              <input
                id="backfill-email-input"
                type="email"
                className={styles.input}
                value={backfillEmail}
                onChange={(e) => {
                  setBackfillEmail(e.target.value);
                  setValidationError(null);
                  setError(null);
                }}
                placeholder="e.g. customer@example.com"
                maxLength={254}
                disabled={isLoading}
              />

              <label htmlFor="backfill-name-input" className={styles.label}>
                Customer Name <span className={styles.optional}>(optional)</span>
              </label>
              <input
                id="backfill-name-input"
                type="text"
                className={styles.input}
                value={backfillName}
                onChange={(e) => {
                  setBackfillName(e.target.value);
                  setError(null);
                }}
                placeholder="e.g. Annie Livermore"
                disabled={isLoading}
              />

              <label htmlFor="backfill-date-input" className={styles.label}>
                Order Date
              </label>
              <input
                id="backfill-date-input"
                type="date"
                className={styles.input}
                value={backfillDate}
                onChange={(e) => {
                  setBackfillDate(e.target.value);
                  setValidationError(null);
                  setError(null);
                }}
                disabled={isLoading}
              />

              <label htmlFor="backfill-fulfilment-input" className={styles.label}>
                Fulfilment <span className={styles.optional}>(optional)</span>
              </label>
              <select
                id="backfill-fulfilment-input"
                className={styles.input}
                value={backfillFulfilment}
                onChange={(e) => {
                  setBackfillFulfilment(e.target.value);
                  setError(null);
                }}
                disabled={isLoading}
              >
                <option value="">Unfulfilled</option>
                <option value="partial">Partial</option>
                <option value="fulfilled">Fulfilled</option>
              </select>
            </>
          )}

          {mode === 'walkin' && (
            <>
              <p className={styles.hint}>
                For a customer who came into the shop and has no Shopify order. The order
                is filed under their Shopify customer account (found by email, or created
                if they don&apos;t have one) so they can sign in, see their scans and pay
                for prints as normal.
              </p>

              <label htmlFor="walkin-email-input" className={styles.label}>
                Customer Email
              </label>
              <input
                id="walkin-email-input"
                ref={inputRef}
                type="email"
                className={styles.input}
                value={walkinEmail}
                onChange={(e) => {
                  setWalkinEmail(e.target.value);
                  setValidationError(null);
                  setError(null);
                }}
                placeholder="e.g. customer@example.com"
                maxLength={254}
                disabled={isLoading}
                aria-invalid={!!validationError}
                aria-describedby={validationError ? 'validation-error' : undefined}
              />

              <label htmlFor="walkin-name-input" className={styles.label}>
                Customer Name <span className={styles.optional}>(optional)</span>
              </label>
              <input
                id="walkin-name-input"
                type="text"
                className={styles.input}
                value={walkinName}
                onChange={(e) => {
                  setWalkinName(e.target.value);
                  setError(null);
                }}
                placeholder="e.g. Annie Livermore"
                disabled={isLoading}
              />

              <label htmlFor="walkin-label-input" className={styles.label}>
                Reference <span className={styles.optional}>(optional)</span>
              </label>
              <input
                id="walkin-label-input"
                type="text"
                className={styles.input}
                value={walkinLabel}
                onChange={(e) => {
                  setWalkinLabel(e.target.value);
                  setError(null);
                }}
                placeholder="e.g. WALKIN-0919"
                maxLength={64}
                disabled={isLoading}
              />
            </>
          )}

          {validationError && (
            <p id="validation-error" className={styles.validationError} role="alert">
              {validationError}
            </p>
          )}

          {error && (
            <p className={styles.apiError} role="alert">
              {error}
            </p>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button type="submit" className={styles.submitButton} disabled={isSubmitDisabled}>
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
